// Jednoduchy WYSIWYG editor pro textova pole (popisy, poznamky, report).
//
// - Format dat je uzky podmnozina HTML: <strong> <em> <u> <br> <p> <ul> <ol> <li>.
//   Nic jineho (odkazy, obrazky, style, barvy) neprojde ani editorem, ani
//   sanitizaci pri zobrazeni — v PDF by to nebylo reprodukovatelne.
// - Stara data jsou plain text s \n. Rozpoznavame je podle absence povolenych
//   tagu a prevadime na odstavce, takze zadna migrace DB neni potreba.
// - Backend sanitizuje stejnou whitelist znovu (API je volatelne i mimo UI),
//   tohle je jen prvni linie + korektni vstup do editoru.

import { useEffect, useMemo, useRef } from 'react';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Bold, Italic, List, ListOrdered, RemoveFormatting, Underline } from 'lucide-react';
import { cn } from '@/lib/utils';

const POVOLENE_TAGY = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'BR', 'P', 'UL', 'OL', 'LI']);
const ZAHODIT_CELE = new Set([
  'SCRIPT',
  'STYLE',
  'IFRAME',
  'OBJECT',
  'EMBED',
  'SVG',
  'MATH',
  'LINK',
  'META',
  'TEMPLATE',
]);

// Rozpoznani "uz je to nase HTML" vs "stary plain text". Zamerne hleda jen
// povolene tagy — text jako "trubka < 50 mm" tak zustane plain textem.
const RICH_TAG_RE = /<\/?(?:b|strong|i|em|u|br|p|ul|ol|li)\b[^>]*>/i;

export function jeRichHtml(value: string): boolean {
  return RICH_TAG_RE.test(value);
}

/** Odstrani vse mimo whitelist tagu a smaze vsechny atributy. */
export function sanitizeRichHtml(html: string): string {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return '';
  vycistiPotomky(root);
  return root.innerHTML;
}

function vycistiPotomky(el: Element): void {
  for (const dite of Array.from(el.children)) {
    if (ZAHODIT_CELE.has(dite.tagName)) {
      dite.remove();
      continue;
    }
    vycistiPotomky(dite);
    if (!POVOLENE_TAGY.has(dite.tagName)) {
      // Nepovoleny obal (div, span, font, table…) zahodime, obsah necháme.
      dite.replaceWith(...Array.from(dite.childNodes));
      continue;
    }
    for (const attr of Array.from(dite.attributes)) {
      dite.removeAttribute(attr.name);
    }
  }
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Stary plain text → odstavce (prazdny radek oddeluje odstavce, jinak <br>). */
export function plainToRichHtml(text: string): string {
  const trimmed = text.replace(/\r\n/g, '\n').trim();
  if (trimmed === '') return '';
  return trimmed
    .split(/\n{2,}/)
    .map((odstavec) => `<p>${escapeHtml(odstavec).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/** Hodnota z API → bezpecne HTML pro editor i zobrazeni. */
export function normalizeRichValue(value: string | null | undefined): string {
  if (!value) return '';
  return jeRichHtml(value) ? sanitizeRichHtml(value) : plainToRichHtml(value);
}

/** HTML → cisty text (nahledy v tabulkach, title atributy, kopie do formulare). */
export function richToPlain(value: string | null | undefined): string {
  if (!value) return '';
  if (!jeRichHtml(value)) return value;
  const sBrs = value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(?:p|li|ul|ol)>/gi, '\n');
  const doc = new DOMParser().parseFromString(sBrs, 'text/html');
  return (doc.body.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim();
}

export function jeRichPrazdny(value: string | null | undefined): boolean {
  return richToPlain(value).trim() === '';
}

/** Blok "nazev tucne + telo pod nim" — pouziva se pro predvolby z ciselniku. */
export function richBlokSNazvem(nazev: string, telo?: string | null): string {
  return `<p><strong>${escapeHtml(nazev.trim())}</strong></p>${normalizeRichValue(telo)}`;
}

/** Prida blok na konec obsahu; do prazdneho pole ho jen vlozi. */
export function richPripoj(soucasny: string, blok: string): string {
  return jeRichPrazdny(soucasny) ? blok : normalizeRichValue(soucasny) + blok;
}

/** Hodnota pro odeslani na API — prazdny obsah posilame jako null. */
export function richKUlozeni(value: string): string | null {
  return jeRichPrazdny(value) ? null : sanitizeRichHtml(value);
}

// ---------------------------------------------------------------------------
// Zobrazeni
// ---------------------------------------------------------------------------

interface RichTextViewProps {
  value: string | null | undefined;
  className?: string;
  /** Co zobrazit, kdyz je hodnota prazdna. Default: nic. */
  fallback?: React.ReactNode;
}

export function RichTextView({ value, className, fallback = null }: RichTextViewProps) {
  const html = useMemo(() => normalizeRichValue(value), [value]);
  if (html === '') return <>{fallback}</>;
  return (
    <div
      className={cn('rich-text text-sm', className)}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

interface RichTextProps {
  id?: string;
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Tailwind class s min-vyskou plochy pro psani. */
  minHeightClass?: string;
  className?: string;
}

// Editoru dovolime jen to, co umime zobrazit i vytisknout do PDF.
// Kazda instance editoru dostava vlastni extensiony — TipTap si do nich uklada
// odkaz na svuj editor, takze sdilena instance by pri vice editorech na strance
// (dialog reportu ma ctyri) patrila poslednimu z nich.
function vytvorExtensions() {
  return [
    StarterKit.configure({
      heading: false,
      blockquote: false,
      code: false,
      codeBlock: false,
      horizontalRule: false,
      strike: false,
      link: false,
    }),
  ];
}

export function RichText({
  id,
  value,
  onChange,
  disabled = false,
  placeholder,
  minHeightClass = 'min-h-[5rem]',
  className,
}: RichTextProps) {
  const extensions = useMemo(() => vytvorExtensions(), []);
  // Posledni znama hodnota z propsu. TipTap vystreli `update` uz pri vytvoreni
  // editoru (normalizace prazdneho dokumentu) — bez teto pojistky by se timto
  // prazdnym updatem prepsala hodnota, kterou parent zaroven nacetl z API.
  const hodnotaRef = useRef(value);
  hodnotaRef.current = value;
  const editor = useEditor({
    extensions,
    content: normalizeRichValue(value),
    editable: !disabled,
    editorProps: {
      attributes: {
        class: cn('rich-text w-full px-3 py-2 text-sm focus:outline-none', minHeightClass),
        ...(id ? { id } : {}),
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.isEmpty ? '' : ed.getHTML();
      if (html === hodnotaRef.current) return;
      onChange(html);
    },
  });

  // Externi zmena hodnoty (nacteni z API, reset formulare). Pri vlastnim psani
  // se `value` uz rovna getHTML(), takze se sem nedostaneme a kurzor neposkoci.
  useEffect(() => {
    if (!editor) return;
    const next = normalizeRichValue(value);
    const current = editor.isEmpty ? '' : editor.getHTML();
    if (next === current) return;
    editor.commands.setContent(next, { emitUpdate: false });
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  // Editor je pri prvnim renderu jeste null — selektor to musi unest.
  const stav = useEditorState({
    editor,
    selector: ({ editor: ed }) =>
      ed
        ? {
            bold: ed.isActive('bold'),
            italic: ed.isActive('italic'),
            underline: ed.isActive('underline'),
            bulletList: ed.isActive('bulletList'),
            orderedList: ed.isActive('orderedList'),
            empty: ed.isEmpty,
          }
        : null,
  });

  return (
    <div
      className={cn(
        'rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring',
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-0.5 border-b border-input px-1.5 py-1">
        <ToolbarButton
          label="Tučně (Ctrl+B)"
          active={stav?.bold}
          disabled={disabled}
          onClick={() => editor?.chain().focus().toggleBold().run()}
        >
          <Bold className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Kurzíva (Ctrl+I)"
          active={stav?.italic}
          disabled={disabled}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
        >
          <Italic className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Podtrženě (Ctrl+U)"
          active={stav?.underline}
          disabled={disabled}
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
        >
          <Underline className="h-4 w-4" />
        </ToolbarButton>
        <span className="mx-1 h-4 w-px bg-border" />
        <ToolbarButton
          label="Odrážky"
          active={stav?.bulletList}
          disabled={disabled}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
        >
          <List className="h-4 w-4" />
        </ToolbarButton>
        <ToolbarButton
          label="Číslovaný seznam"
          active={stav?.orderedList}
          disabled={disabled}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
        >
          <ListOrdered className="h-4 w-4" />
        </ToolbarButton>
        <span className="mx-1 h-4 w-px bg-border" />
        <ToolbarButton
          label="Zrušit formátování"
          disabled={disabled}
          onClick={() => editor?.chain().focus().unsetAllMarks().clearNodes().run()}
        >
          <RemoveFormatting className="h-4 w-4" />
        </ToolbarButton>
      </div>
      <div className="relative">
        <EditorContent editor={editor} />
        {placeholder && stav?.empty && (
          <div className="pointer-events-none absolute left-3 top-2 text-sm text-muted-foreground">
            {placeholder}
          </div>
        )}
      </div>
    </div>
  );
}

interface ToolbarButtonProps {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

function ToolbarButton({ label, active, disabled, onClick, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active ?? false}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors',
        'hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none',
        active && 'bg-accent text-accent-foreground',
      )}
    >
      {children}
    </button>
  );
}
